import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './PoliticalWins.css';

const PoliticalWins = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [counters, setCounters] = useState({
    campaigns: 0,
    politicians: 0,
    winRatio: 0,
    states: 0
  });

  const targetValues = {
    campaigns: 88,
    politicians: 5500,
    winRatio: 79,
    states: 15
  };

  useEffect(() => {
    if (inView) {
      const animateCounters = () => {
        const duration = 2000;
        const steps = 60;
        const stepDuration = duration / steps;

        let currentStep = 0;
        const interval = setInterval(() => {
          currentStep++;
          const progress = currentStep / steps;

          setCounters({
            campaigns: Math.floor(targetValues.campaigns * progress),
            politicians: Math.floor(targetValues.politicians * progress),
            winRatio: Math.floor(targetValues.winRatio * progress),
            states: Math.floor(targetValues.states * progress)
          });

          if (currentStep >= steps) {
            clearInterval(interval);
            setCounters(targetValues);
          }
        }, stepDuration);
      };

      animateCounters();
    }
  }, [inView]);

  // Political party logos with actual image files
  const partyLogos = [
    { name: 'BJP', logo: '/politicalparty/BJP.jpg', displayName: 'BJP' },
    { name: 'INC', logo: '/politicalparty/inc.png', displayName: 'Congress' },
    { name: 'AAP', logo: '/politicalparty/aap.jpg', displayName: 'AAP' },
    { name: 'BSP', logo: '/politicalparty/bsp.jpg', displayName: 'BSP' },
    { name: 'SP', logo: '/politicalparty/SAMAJWADI PARTY.png', displayName: 'Samajwadi Party' },
    { name: 'BJD', logo: '/politicalparty/BJD.png', displayName: 'BJD' },
    { name: 'TMC', logo: '/politicalparty/TMC.png', displayName: 'TMC' },
    { name: 'RJD', logo: '/politicalparty/RJD.jpg', displayName: 'RJD' },
    { name: 'JDU', logo: '/politicalparty/JDU.jpg', displayName: 'JD(U)' },
    { name: 'NCP', logo: '/politicalparty/NCP.jpg', displayName: 'NCP' },
    { name: 'SHIV SENA', logo: '/politicalparty/SHIV SENA.png', displayName: 'Shiv Sena' },
    { name: 'TDP', logo: '/politicalparty/TDP.jpg', displayName: 'TDP' },
    { name: 'YSR CONGRESS', logo: '/politicalparty/YSR CONGRESS.jpg', displayName: 'YSRCP' },
    { name: 'AIMIM', logo: '/politicalparty/AIMIM.png', displayName: 'AIMIM' },
    { name: 'RLP', logo: '/politicalparty/RLP ( Rastriya Lok Tantrik Party).png', displayName: 'RLP' },
    { name: 'APNA DAL', logo: '/politicalparty/APNA DAL.png', displayName: 'Apna Dal' },
    { name: 'RLD', logo: '/politicalparty/rld.jpg', displayName: 'RLD' },
    { name: 'LJD', logo: '/politicalparty/ljd.jpg', displayName: 'LJD' },
    { name: 'JAN SURAJ', logo: '/politicalparty/jan suraj.png', displayName: 'Jan Suraj' },
    { name: 'BJYM', logo: '/politicalparty/BJYM.png', displayName: 'BJYM' },
    { name: 'NSUI', logo: '/politicalparty/NSUI.png', displayName: 'NSUI' },
    { name: 'ABVP', logo: '/politicalparty/abvp.jpg', displayName: 'ABVP' },
  ];

  // Duplicate the array for seamless infinite scroll
  const duplicatedLogos = [...partyLogos, ...partyLogos];

  return (
    <section id="wins" className="py-20 md:py-32 bg-gradient-premium">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">

        {/* Enhanced Party Logos with Horizontal Scrolling Animation */}
        <motion.div
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"

        >
          <div className="text-center mb-8 md:mb-12">
            <motion.h2
            ref={ref}
            className="display-text text-center text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
           Trusted Across
            <span className="text-gradient-political"> Party Lines</span>
          </motion.h2>
            <p className="text-lg text-gray-600">Our non-partisan approach has earned trust from leaders across the political spectrum</p>
          </div>

          {/* Scrolling Container */}
          <div className="relative overflow-hidden political-logos-container">
            {/* Gradient overlays for smooth fade effect */}
            <div className="absolute left-0 top-0 w-16 md:w-20 h-full scroll-fade-left z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 w-16 md:w-20 h-full scroll-fade-right z-10 pointer-events-none"></div>

            {/* Scrolling logos container */}
            <div className="flex items-center py-4 h-64 md:h-64 ">
              <motion.div
                className="flex items-center space-x-6 md:space-x-8 political-logos-scroll"
                animate={{
                  x: [0, -((120 + 32) * partyLogos.length)], // Move by the width of original logos array
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 40, // 40 seconds for complete cycle - slower for better readability
                    ease: "linear",
                  },
                }}
                style={{
                  width: `${(320 + 32) * duplicatedLogos.length}px`, // 120px logo width + 32px spacing
                  willChange: 'transform' // Optimize for animations
                }}
              >
                {duplicatedLogos.map((party, index) => (
                  <motion.div
                    key={`${party.name}-${index}`}
                    className="flex-shrink-0 w-48 h-48 md:w-48 md:h-48 bg-white rounded-2xl flex items-center justify-center shadow-lg border border-gray-100 cursor-pointer group party-logo-item"
                    whileHover={{
                      scale: 1.05,
                      y: -3,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <img
                      src={party.logo}
                      alt={`${party.displayName} Logo`}
                      className="w-48 h-48 md:w-48 md:h-48 object-contain rounded-lg party-logo-image"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                      onLoad={(e) => {
                        // Ensure fallback is hidden when image loads successfully
                        const fallback = e.target.nextElementSibling;
                        if (fallback) {
                          fallback.style.display = 'none';
                        }
                      }}
                    />
                    {/* Fallback text display */}
                    <div className="w-full h-full text-xs font-bold text-gray-600 text-center p-2">
                      <span className="leading-tight">{party.displayName}</span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Additional info */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              Representing diverse political ideologies across India's democratic landscape
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PoliticalWins;
